import TrackingLastPositionBusesService from 'js/services/api/tracking/last-position-buses.js'
import PositionBusIconRules from 'js/services/position-bus-icon-rules'

export default {
  data() {
    return {
      // Información de sublínea

      sublineBuses: {},
      wsSublineConnection: null,
      wsSublineEventsReceived: 0,

      // Información de ruta

      routeBuses: {},
      wsRouteConnection: null,
      wsRouteEventsReceived: 0,

      // cache
      lastRenderedStopTimes: {},
      selectedStopId: null
    }
  },

  destroyed() {
    this.disconnectSublineWebSocket()
    this.disconnectRouteWebSocket()
  },

  methods: {
    /*
     * Pinta los autobuses con los datos del backend de este momento y
     * se conecta al canal de WS para actualizar sus posiciones a partir de este momento
     */
    getRouteBuses(routeId) {
      this.disconnectRouteWebSocket()

      if (routeId) {
        const routeBuses = {}

        this.wsRouteConnection = Vue.cable.subscriptions.create({
          channel: 'PositionBusChannel',
          room: routeId
        }, {
          received: data => {
            this.updateWsPositionsBuses(routeId, data, this.routeBuses, this.wsRouteEventsReceived)

            this.onWebSocketUpdate('route')
          }
        })

        const params = {
          template: 'passenger_route',
          route_id: routeId,
          calculated: true
        }

        TrackingLastPositionBusesService.index({params, skipLoading: true}).then(result => {
          _.each(result.data, positionBus => {
            const position = this.positionBusRepresentation(positionBus)

            if (this.invalidPosition(position)) return

            routeBuses[position.bus.id] = position
          })

          this.$set(this, 'routeBuses', routeBuses)
        })
      }
    },

    getSublineBuses(sublineId) {
      this.disconnectSublineWebSocket()

      if (sublineId) {
        const sublineBuses = {}

        this.wsSublineConnection = Vue.cable.subscriptions.create({
          channel: 'PositionBusSublineChannel',
          room: sublineId
        }, {
          received: data => {
            this.updateWsPositionsBuses(
              sublineId, data, this.sublineBuses, this.wsSublineEventsReceived
            )

            this.onWebSocketUpdate('subline')
          }
        })

        const params = {
          template: 'passenger_route',
          subline_id: sublineId,
          calculated: true
        }

        TrackingLastPositionBusesService.index({params, skipLoading: true}).then(result => {
          _.each(result, positionBus => {
            const position = this.positionBusRepresentation(positionBus)
            if (this.invalidPosition(position)) return

            sublineBuses[position.bus.id] = position
          })

          this.$set(this, 'sublineBuses', sublineBuses)
        })
      }
    },

    // Decide si es necesario refrescar el popup cuando llegan nuevos datos por WebSocket
    onWebSocketUpdate(type) {
      if (!this.selectedStopId) return

      if (type === 'route') {
        if (this.shouldRefreshPopup(this.selectedStopId, this.routeBuses, type)) {
          this.refreshOpenStopPopup(type)
        }
      }

      if (type === 'subline') {
        if (this.shouldRefreshPopup(this.selectedStopId, this.sublineBuses, type)) {
          this.refreshOpenStopPopup(type)
        }
      }
    },

    // Compara los tiempos actuales con los últimos renderizados para evitar refrescos innecesarios
    shouldRefreshPopup(stopId, positionBuses, type) {
      const newTimes = this.getStopTimesForPopup(stopId, positionBuses, type)
      const oldTimes = this.lastRenderedStopTimes[`${type}_${stopId}`]

      if (!_.isEqual(newTimes, oldTimes)) {
        this.lastRenderedStopTimes[`${type}_${stopId}`] = newTimes

        return true
      }

      return false
    },

    // Fuerza el re-render del popup abierto de la parada seleccionada
    refreshOpenStopPopup(type) {
      if (!this.selectedStopId) return

      const stopsList = type === 'route' ? this.stops : this.sublineStops
      const stop = stopsList.find(s => s.id === this.selectedStopId)

      if (!stop || !stop.marker) return

      // Re-renderizamos el popup
      if (type === 'route') {
        this.onRouteStopClick(stop.marker)
      } else {
        this.onSublineStopClick(stop.marker)
      }
    },

    // Obtiene y ordena los tiempos (en minutos) de llegada de los buses para una parada específica
    getStopTimesForPopup(stopId, positionBuses, type) {
      const stopsList = type === 'route' ? this.stops : this.sublineStops
      const stop = stopsList.find(s => s.id === stopId)
      if (!stop) return []

      const stopTimes = []

      _.each(positionBuses, positionBus => {
        if (!positionBus || !positionBus.times) return

        const seconds = positionBus.times[stop.code]
        if (typeof seconds === 'number' && seconds >= 0 && seconds <= 3000) {
          stopTimes.push(Math.floor(seconds / 60))
        }
      })

      return _.sortBy(stopTimes)
    },

    // Indica si el popup de una parada está actualmente abierto en el mapa
    isPopupOpen(stop) {
      return stop.marker?.getPopup()?.isOpen()
    },

    invalidPosition(positionBus) {
      return (
        positionBus.out_of_route || positionBus.no_service ||
        positionBus.position.lat === undefined || positionBus.position.lng === undefined ||
        (positionBus.position.lat === 0.0 && positionBus.position.lng === 0.0) ||
        this.inactiveBus(positionBus)
      )
    },

    inactiveBus(positionBus) {
      return moment(positionBus.last_valid_datetime).diff(moment().subtract(5, 'm'), 'm') < 0
    },

    disconnectSublineWebSocket() {
      if (this.wsSublineConnection) {
        Vue.cable.subscriptions.remove(this.wsSublineConnection)
        this.wsSublineConnection.consumer.disconnect()
        this.$set(this, 'sublineBuses', {})
        this.wsSublineConnection = undefined
      }
    },

    disconnectRouteWebSocket() {
      if (this.wsRouteConnection) {
        Vue.cable.subscriptions.remove(this.wsRouteConnection)
        this.wsRouteConnection.consumer.disconnect()
        this.$set(this, 'routeBuses', {})
        this.wsRouteConnection = undefined
      }
    },

    /*
     * Actualiza la posición de los autobuses con la información recibida por WS
     */
    updateWsPositionsBuses(routeId, data, busesList, numberOfEvents) { // eslint-disable-line
      _.each(data.data, positionBus => {
        const position = this.positionBusRepresentation(positionBus)

        if (position.out_of_route || position.no_service) {
          this.$set(busesList, position.bus.id, undefined)
          return
        }

        // Eliminamos los tiempos si es un autobús inactivo (se pinta en gris)
        if (moment(position.datetime).diff(moment().subtract(2, 'm'), 'm') < 0) {
          position.times = {}
        }

        numberOfEvents += 1

        if (busesList[position.bus.id]) {
          this.$set(
            busesList,
            position.bus.id,
            _.merge(busesList[position.bus.id], position)
          )
        } else {
          this.$set(busesList, position.bus.id, position)
        }
      })
    },

    positionBusRepresentation(positionBus) {
      const bus = {
        id: positionBus.bus_id,
        plate: positionBus.bus_plate,
        wheelchair_accessible: positionBus.bus_wheelchair_accessible,
        sanitized: positionBus.bus_sanitized,
        occupancy_level: positionBus.bus_occupancy_level,
        orientation: positionBus.bus_orientation || positionBus.orientation
      }

      return {
        ...positionBus,
        bus,
        icon: this.busIcon({bus}),
        position: {
          lat: positionBus.lat,
          lng: positionBus.lng
        }
      }
    },

    /*
     * Devuelve el icono de cada autobús
     */
    busIcon(data) {
      const BusIcon = L.DivIcon.extend({
        options: {
          data: null,
          className: `leaflet-position-bus ${PositionBusIconRules.mapIconClasses(data.bus)}`,
          html: "<i class='ns-icon-bus'></i><div class='services'><div class='accessible-icon'></div><div class='sanitized-icon'></div></div>", // eslint-disable-line
        }
      })

      return new BusIcon({
        data
      })
    }
  }
}
